import { create, tsx } from '@dojo/framework/core/vdom';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { FAQ } from '../../widgets/FAQ';
import { Hero } from '../../widgets/Hero';
import { Summary } from '../../widgets/Summary';

import Head from '../../widgets/Head';
import { IndustryExpertise } from '../../widgets/IndustryExpertise';
import ContentWrapper from '../../widgets/ContentWrapper';
import Image from '../../widgets/Image';
import Section from '../../widgets/Section';
import ListColumns from '../../widgets/ListColumns';
import SummaryList from '../../widgets/SummaryList';
import SitePenLogo from '../../widgets/SitepenLogo';

const customSoftwareImg = require('../../assets/images/icon-illustration/custom-software.svg');
const enterpriseUXResearch = require('../../assets/images/icon-illustration/enterprise-ux-research.svg');
const backEndServicesImg = require('../../assets/images/icon-illustration/back-end-services.svg');
const teamEfficiencyImg = require('../../assets/images/illustration/team-efficiency.svg');
const angularFeatureImg = require('../../assets/images/illustration/angular-feature.svg');
const angularDevelopmentImg = require('../../assets/images/illustration/angular-development.svg');
const angularTechnologiesImg = require('../../assets/images/logo-illustration/angular-technologies.svg');

const factory = create();

export const AngularDevelopment = factory(function AngularDevelopment() {
	return (
		<div>
			<Head
				title="Angular Development | SitePen"
				description="Angular js web development company building enterprise solutions to improve processes and empower business success"
			/>

			<Hero background="level3Red">
				{{
					xlTitle: (
						<div>
							Angular
							<br />
							Development
						</div>
					),
					blurb:
						"The best software applications go beyond allowing the users to do their work; they deliver fast, responsive experiences that don't waste the user's time."
				}}
			</Hero>

			<Section type="leading">
				<ContentWrapper>
					<SummaryList
						style="alternating-reverse"
						listGuttering="none"
						trailingBias="medium"
						itemPaddingBottom="md"
					>
						{{
							items: [
								{
									title: 'Dynamic Angular Development',
									blurb: (
										<virtual>
											<p>
												When Google introduced AngularJS in 2010, it
												revolutionized the world of front-end web
												application development. Its adoption of two-way
												data binding between JavaScript code and HTML
												simplified and accelerated the creation of
												applications. However, AngularJS encountered
												architectural issues that limited the scale of
												application that it could reasonably handle.
											</p>
											<p>
												Today's Angular framework leveraged the lessons
												learned from its predecessor and the web development
												community at large to develop a robust system that
												can support the creation of the most extensive and
												most complicated enterprise application. It
												re-envisions the two-way data binding system of its
												predecessor into a unidirectional data flow pattern
												that has yielded dramatic improvements in
												performance and application efficiency. Angular also
												bundles in some of the most cutting-edge
												technologies for modern web applications, such as
												using TypeScript to enforce type safety; RxJS to
												simplify the introduction of complex business logic
												and data transformations; and first-class support
												for powerful bundling tools such as webpack.
											</p>
											<p>
												The resulting framework is one of the most powerful
												and sophisticated options that today's web
												developers have available to them. Angular is a
												complex tool, but when used by an experienced
												development team, it can quickly deliver world-class
												performance, stability, and maintainability.
											</p>
										</virtual>
									),
									image: <Image src={angularFeatureImg} size="lg" />
								},
								{
									blurb: (
										<virtual>
											<p>
												The key to unlocking Angular's power lies in the
												knowledge and experience of the development team.
												SitePen has been working with Angular applications
												since it was first released and has fostered
												relationships with many developers that continue to
												work on Angular today. These relationships grant
												SitePen a deep understanding of the framework that
												goes beyond documentation and experience. When faced
												with a new feature or unusual behavior, we simply
												ask the developers that created that feature to
												understand the intent behind it or, occasionally,
												uncover a defect.
											</p>
											<p>
												SitePen also believes that the best time to discover
												what a tool like Angular can do is before we need
												it. Our engineers experiment with new features as
												they are being developed. We often create simple
												prototypes to help us understand the capabilities
												that are continually being added to Angular and
												share the results within the organization. By doing
												our homework beforehand, we can better advise our
												clients on the best way to implement the solution
												they are looking for.
											</p>
										</virtual>
									),
									image: <SitePenLogo />
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<SummaryList>
						{{
							title: 'Angular Development Services',
							blurb: (
								<virtual>
									We offer a variety of services to address whatever need you have
									with your Angular applications.
								</virtual>
							),
							items: [
								{
									title: 'Angular Front-End Development',
									blurb: (
										<virtual>
											<p>
												Application development is the most common service
												that we provide to clients that have selected
												Angular for their application's front ends. Our
												team's extensive experience allows us to analyze the
												business requirements and develop an optimal
												architecture to meet those requirements in a clear,
												performant, and maintainable fashion. We leverage
												Angular's powerful module system to break the
												application into sub-domains that can be optimized
												for a specific set of related features yielding a
												highly cohesive yet decoupled system that encourages
												extension and simplifies maintenance.
											</p>
											<p>
												Our engineers are proponents of testing their code,
												a philosophy that Angular strongly supports. We
												don't merely deliver solutions that work; we prove
												that they work by supporting the production code
												with a comprehensive, multi-layer test suite
												designed to identify defects during development and
												catch regressions as the application's codebase
												evolves.
											</p>
										</virtual>
									),
									image: (
										<Image
											src={customSoftwareImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								},
								{
									title: 'Angular Consulting Services',
									blurb: (
										<virtual>
											<p>
												Every client and every project have a unique set of
												opportunities and risks associated with them. We
												recognize that some of these challenges require a
												more targeted approach than our full-service
												development offering. Our team can step into any
												phase of the project and ensure that it meets all
												its objectives. The primary areas that we support
												are requirements gathering, system design,
												application development, testing, and performance
												optimization.
											</p>
											<p>
												Our engineering and project management teams have a
												clear understanding of what needs to be accomplished
												in each phase of the project. They also have the
												knowledge and experience to develop a plan to ensure
												that those requirements are met. These intense,
												focused efforts often take a failing project and set
												it on the path to success.
											</p>
										</virtual>
									),
									image: (
										<Image
											src={enterpriseUXResearch}
											lazy
											size="icon"
											align="center"
										/>
									)
								},
								{
									title: 'Angular Migration Services',
									blurb:
										"Like many of today's software technologies, Angular is rapidly evolving to meet the growing demands placed on computer-assisted automation. Keeping up with these changes generally improves the quality of the application as defects are corrected, security holes are closed, and new features are added to simplify the creation of new capabilities. However, it can be challenging to keep a suite of applications up to date with the latest version of the framework. Our team monitors the Angular project as it evolves and ensures that we understand how to shepherd a codebase through the migration process without introducing regressions.",
									image: (
										<Image
											src={backEndServicesImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper>
					<Summary reverse collapse="large">
						{{
							title: 'Technologies We Use',
							blurb: (
								<virtual>
									<p>
										We leverage Angular's entire development stack to deliver
										the ideal solution for any application.
									</p>
									<ListColumns>
										{[
											'Angular',
											'TypeScript',
											'Ngrx',
											'Rxjs',
											'Angular Material',
											'Karma',
											'Jasmine',
											'Mocha',
											'Protractor'
										]}
									</ListColumns>
								</virtual>
							),
							image: <Image src={angularTechnologiesImg} lazy size="lg" />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<IndustryExpertise />
			</Section>
			<Section>
				<ContentWrapper>
					<SummaryList
						style="alternating"
						listGuttering="none"
						trailingBias="medium"
						itemPaddingBottom="md"
					>
						{{
							items: [
								{
									title: 'Choosing the Right Angular Development Team',
									blurb:
										"Angular is not just a framework; it encompasses an entire ecosystem of frameworks and subsystems that enable creating applications that support the most complicated business processes. The right angular development team knows how each of these components fits together and has the experience to select and configure these components to simplify the development process. The right team for an Angular project can also break down large business processes into sub-domains and design specialized modules that encapsulate each subdomain's business rules in a loosely coupled yet cohesive manner. When implemented by a skilled team of developers, these designs result in clear, well-organized code that is easily understood and maintained.",
									image: <Image src={teamEfficiencyImg} lazy size="lg" />
								},
								{
									title: 'Benefits of Using Angular Development',
									blurb:
										"The vast nature of the Angular ecosystem makes it difficult for many enterprise developers to cultivate and sustain the deep understanding required to make the most out of it, especially when they are also trying to address the day-to-day needs of the existing software suite. An external partner, like SitePen, has the advantage of focusing on the technology itself. Our company adds value by maintaining expert-level knowledge of Angular and how to use it to address our client's needs. When our Angular expertise is paired with our client's domain knowledge, world-class solutions are the result.",
									image: <Image src={angularDevelopmentImg} lazy size="md" />
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ConnectStrip />
			</Section>
			<Section trailing>
				<FAQ title="Angular FAQs">
					{[
						{
							title: 'What Is Angular Used For?',
							content: (
								<p>
									Angular is designed to enable the creation of large-scale
									applications that support complex business processes. Its
									extensive feature set allows developers to create highly
									modularized solutions that facilitate the authoring of code that
									is both testable and maintainable. While Angular can be used to
									write applications of any complexity level, its relatively steep
									learning curve tends to pair better with larger applications
									that can take full advantage of Angular's ability to break down
									large business domains into smaller, more manageable
									sub-domains.
								</p>
							)
						},
						{
							title: 'Why Use Angular for Front-End Development?',
							content: (
								<p>
									Angular is fully equipped to address the three primary concerns
									of today's front-end web applications. First, it contains superb
									tooling to connect intelligent HTML templates with
									TypeScript-based components. Together, the templates and
									components enable dynamic user experiences to be created clearly
									and logically. The middle layer of the front-end application
									houses the business logic and validation rules. Angular's use of
									unidirectional data flows, and reactive programming frameworks
									allow developers to write these algorithms in a manner that
									supports code reuse and testability. Finally, the application's
									server-facing aspect is simplified through the use of powerful
									abstractions that allows developers to interact with the server
									without getting bogged down in implementation details such as
									message encoding/decoding and handling server-generated response
									codes. By combining all of these characteristics, Angular
									provides a single, consistent experience to developers
									regardless of which aspect of the application they are working
									on.
								</p>
							)
						}
					]}
				</FAQ>
			</Section>
		</div>
	);
});

export default AngularDevelopment;
